import React from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { useApiError } from '@/modules/common/hooks/use-api-error';

interface ErrorStateAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'outline' | 'destructive' | 'secondary' | 'ghost' | 'link';
  isLoading?: boolean;
  icon?: React.ReactNode;
}

interface ErrorStateProps {
  error?: Error | string | null;
  title?: string;
  message?: string;
  action?: ErrorStateAction;
  actions?: ErrorStateAction[];
  className?: string;
}

interface ErrorDisplayParams {
  error?: Error | string | null;
  title?: string;
  message?: string;
  t: (key: string) => string;
  getApiErrorMessage: (error: unknown) => string;
}

/**
 * A reusable error state component for displaying errors with optional actions
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ErrorState error={error} />
 *
 * // With single action (backward compatibility)
 * <ErrorState
 *   error={error}
 *   action={{
 *     label: 'Go Back',
 *     onClick: () => navigate('/dashboard')
 *   }}
 * />
 *
 * // With multiple actions and icons
 * <ErrorState
 *   error={error}
 *   actions={[
 *     {
 *       label: 'Refresh',
 *       onClick: () => window.location.reload(),
 *       variant: 'outline',
 *       icon: <RefreshCw className="w-4 h-4" />
 *     },
 *     {
 *       label: 'Go Back',
 *       onClick: () => navigate('/dashboard'),
 *       variant: 'default',
 *       icon: <ChevronLeft className="w-4 h-4" />
 *     }
 *   ]}
 * />
 *
 * // With custom content, multiple actions, and icons
 * <ErrorState
 *   title="Failed to load data"
 *   message="Unable to fetch project details. Please try again."
 *   actions={[
 *     {
 *       label: 'Retry',
 *       onClick: () => refetch(),
 *       variant: 'default',
 *       icon: <RefreshCw className="w-4 h-4" />
 *     },
 *     {
 *       label: 'Back to Dashboard',
 *       onClick: () => navigate('/dashboard'),
 *       variant: 'outline',
 *       icon: <Home className="w-4 h-4" />
 *     }
 *   ]}
 * />
 * ```
 */
const getErrorDisplay = ({ error, title, message, t, getApiErrorMessage }: ErrorDisplayParams) => {
  // Use custom message if provided, otherwise use getApiErrorMessage for proper localization
  const errorMessage = message || getApiErrorMessage(error) || t('error.reloadPage');
  const errorTitle = title || t('error.generic');
  return { errorMessage, errorTitle };
};

export function ErrorState({
  error,
  title,
  message,
  action,
  actions,
  className = 'min-h-screen',
}: ErrorStateProps) {
  const { t } = useTranslation('common');
  const { getErrorMessage } = useApiError();
  const { errorMessage, errorTitle } = getErrorDisplay({
    error,
    title,
    message,
    t,
    getApiErrorMessage: getErrorMessage,
  });
  // Support both single action (legacy) and multiple actions (new) patterns
  const allActions = actions || (action ? [action] : []);

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className="mx-auto max-w-md px-4 text-center">
        <h3 className="mb-2 text-lg font-medium text-foreground">{errorTitle}</h3>
        <p className="mb-6 text-muted-foreground">{errorMessage}</p>
        {allActions.length > 0 && (
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            {allActions.map((actionItem, index) => {
              // Dynamically choose button component based on loading state requirement
              const ActionComponent = actionItem.isLoading !== undefined ? ButtonLoadable : Button;
              const props =
                actionItem.isLoading !== undefined ? { isLoading: actionItem.isLoading } : {};

              return (
                <ActionComponent
                  key={index}
                  variant={actionItem.variant || 'default'}
                  onClick={actionItem.onClick}
                  {...props}
                >
                  {actionItem.icon && <span className="mr-2">{actionItem.icon}</span>}
                  {actionItem.label}
                </ActionComponent>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
